/**
 * @module AEM
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { Injectable } from '@angular/core';
import { Content } from '@core/aem/models/content.interface';
import { ContentManifest, ContentMetaData } from '@core/aem/models/deprecated-content.interface';
import { HttpEventInterceptorService } from '@core/error-handler/services/http-event-interceptor.service';

import { API_MAP } from '@support/api/api-map';
import { WorkerService } from '@core/worker/services/worker.service';
import { WorkerRequestArgs } from '@core/worker/models/worker-request-args';

import { AppConfig } from './../../../root/app.config';
import { Router } from '@angular/router';
import { APIError } from '@support/api/common/error.interface';
import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { throwError as observableThrowError, Subject, Observable, Observer } from 'rxjs';
import { SeoService } from '@core/services/seo.service';
import { map, catchError } from 'rxjs/operators';

import { SETTINGS, CHANNELS } from '@environment/settings';
import { GlobalErrorService } from '@support/shared/widgets/services/global-error.service';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { PdlWrapperService } from '@core/services/pdl-wrapper.service';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { PDP_CANONICAL_URL_HOLD_PARAM } from '@support/shared/constants/pdl-data.constants';

// It is not considering import as declaration , throwing lint error at import statement itself

/* tslint:disable:no-any */ // Use any to get away from ContentItem Generic, we will remove this service anyways

/**
 * Service for retrieving/accessing content meta data
 * @access protected
 */
@Injectable({
  providedIn: 'root',
})
export class ContentProviderService {
  private contentManifest: ContentManifest;
  private ready: Subject<boolean>;
  private loaded: boolean;
  private url: string;
  public receivedContent: Subject<Content>;
  private alreadyLoadedContentUrl: string;
  /**
   * Placeholder flag to change error page routing if API_999 fails from
   * myTmo App.
   */
  public custProfileFailed: boolean;

  constructor(
    private http: HttpClient,
    private workerService: WorkerService,
    private appConfig: AppConfig,
    private globalErrorService: GlobalErrorService,
    private router: Router,
    private httpEventInterceptorService: HttpEventInterceptorService,
    private seoService: SeoService,
    private pdlService: PdlWrapperService,
    private pdpDataHandlerService: PdpDataHandlerService
  ) {
    this.ready = new Subject<boolean>();
    this.receivedContent = new Subject<Content>();
    this.custProfileFailed = false;
  }

  public watch(): Observable<Content> {
    return this.receivedContent.asObservable();
  }

  /**
   * Function to identify authorable data from AEM response
   * @param endpoint - API endpoint URL for fetching AEM data
   * @return {Observable<Object>}
   *
   */
  public getRequest(endpoint: string): Observable<HttpResponse<Object>> {
    const domain: string = this.appConfig.getConfig('aemEndPoint');
    const aemVersion = this.appConfig.getConfig('aemContentVersion')
      ? this.appConfig.getConfig('aemContentVersion')
      : '1.0';
    // console.error('Do not use this class, it is being deprecated. Use Container Holder');
    const finalURL = domain + endpoint;
    const defaultHeader = new HttpHeaders().append('Accept', 'application/json');
    const workerArg: WorkerRequestArgs = {
      headers: defaultHeader,
      preferCache: true,
      cacheResponse: true,
      cacheKey: finalURL.concat(aemVersion),
    };
    return this.workerService.get(finalURL, workerArg);
  }

  public getContent(id: string): Observable<ContentMetaData> {
    // console.error('Do not use this class, it is being deprecated. Use Container Holder');
    const contentPath = this.getContentPath(id);
    return this.http.get<ContentMetaData>(`./assets/${contentPath}`);
  }

  public checkLoading(): Observable<boolean> {
    // console.error('Do not use this class, it is being deprecated. Use Container Holder');
    if (!this.loaded) {
      return this.ready.asObservable();
    } else {
      return Observable.create((observer: Observer<boolean>) => {
        observer.next(true);
      });
    }
  }

  /**
   * to get data from AEM and parse the data till 'module-enhancements' node.
   * @param {string} pageUrl String dynamic part of AEM call.
   * @param {string} prefix
   * @deprecated this should not be directly called from out component/service.
   * Please use getCMSData instead
   * @return {Observable<Object>}
   */
  public getContentData(
    pageUrl?: string,
    prefix: string = 'shared',
    critical: boolean = true,
    trackPdl: boolean = false
  ): Observable<Object> {
    // console.error('Do not use this class, it is being deprecated. Use Container Holder');
    // need to pick channel from common config
    // needs to finalize the strategy
    /*tslint:disable:cyclomatic-complexity*/
    const contentUrl = this.router.url.split('?')[0];
    this.url = pageUrl
      ? `/content/t-mobile/en/${prefix}/${pageUrl}.content.json`
      : `/content/t-mobile/en/${prefix}${contentUrl}.content.json`;
    return this.getRequest(this.url).pipe(
      map((response: any) => {
        if (!response.ok) {
          // tslint:disable-next-line: no-string-literal
          const errorResponse = response.body.errors.error;
          this.setAEMGlobalError(errorResponse);
          if (!pageUrl) {
            this.receivedContent.next(null);
          }
          if (critical && this.isErrorHandlingEnabled()) {
            this.httpEventInterceptorService.handleHttpError(response, this.url);
          } else {
            throw new Error();
          }
        }
        const data: Content = response.body;
        this.setPdlData(data, pageUrl, contentUrl, trackPdl);
        // Send PDL Config Data to PDL Module for page
        if (!pageUrl) {
          if (data.model['model-data']['pdl-enhancements']) {
            this.pdlService.setEventCaptureConfig(
              data.model['model-data']['pdl-enhancements']['event-config']
            );
          }
          this.receivedContent.next(data);
          // set seo metadata in headers
          this.setSeoData(data);
        } else {
          if (contentUrl) {
            this.setSeoData(data);
          }
          this.pdlService.trackData(pageUrl, 'AEM_COMPONENT_CONFIG', data);
        }
        if (
          data &&
          data.model &&
          data.model['model-data'] &&
          data.model['model-data']['module-enhancements']
        ) {
          return data.model['model-data']['module-enhancements'];
        }
      }),
      catchError((error: Error): Observable<Object> => {
        return this.getAEMRequest(this.url).pipe(
          map((response: HttpResponse<Content>) => {
            const data: Content = response.body;
            if (
              data &&
              data.model &&
              data.model['model-data'] &&
              data.model['model-data']['module-enhancements']
            ) {
              return data.model['model-data']['module-enhancements'];
            }
          })
        );
      })
    );
  }

  private setPdlData(
    data: Content,
    pageUrl?: string,
    contentUrl?: string,
    trackPdl?: boolean
  ): void {
    const canonicalURL = data.metadata[SHARED_CONSTANTS.EN][SHARED_CONSTANTS.CANONICAL]
      ? data.metadata[SHARED_CONSTANTS.EN][SHARED_CONSTANTS.CANONICAL]
      : '';
    const canonicalMatch =
      this.checkCanonicalUrl(contentUrl.trim(), canonicalURL.trim()) ||
      PDP_CANONICAL_URL_HOLD_PARAM.URL.includes(canonicalURL);
    if (
      canonicalURL &&
      ((pageUrl && this.alreadyLoadedContentUrl !== pageUrl) ||
        (!pageUrl && this.alreadyLoadedContentUrl !== contentUrl) ||
        trackPdl ||
        canonicalMatch)
    ) {
      this.alreadyLoadedContentUrl = pageUrl ? pageUrl : contentUrl;
      if (canonicalMatch) {
        if (
          contentUrl !== SHARED_CONSTANTS.RATE_PLANS &&
          contentUrl !== SHARED_CONSTANTS.RATE_SERVICES &&
          contentUrl !== SHARED_CONSTANTS.EFFECTIVE_DATE
        ) {
          this.pdpDataHandlerService.removeFlowName();
        }
        this.pdlService.trackData(contentUrl, 'PAGE_LOAD', data);
        if (
          contentUrl === SHARED_CONSTANTS.RATE_PLANS ||
          contentUrl === SHARED_CONSTANTS.RATE_SERVICES ||
          contentUrl === SHARED_CONSTANTS.EFFECTIVE_DATE
        ) {
          this.pdpDataHandlerService.setFlowName(data.metadata.en['flow-name']);
        }
        const userData = this.pdpDataHandlerService.setUserData();
        this.pdlService.trackData('Product-Detail', 'product', { user: userData });
      }
    }
  }

  /**
   * tTODO: Remove this function and dont have authorable components get their own data.
   * This is only here to unblock SEO fixes for PDP pages
   * @param {string} pageUrl String dynamic part of AEM call.
   * @param {string} prefix
   * @deprecated this should not be directly called from out component/service.
   * Please use getCMSData instead
   * @return {Observable<Object>}
   */
  public getAuthorableComponentData(
    pageUrl?: string,
    prefix: string = 'shared',
    critical: boolean = true,
    trackPdl: boolean = false
  ): Observable<Object> {
    // console.error('Do not use this class, it is being deprecated. Use Container Holder');
    // need to pick channel from common config
    // needs to finalize the strategy
    /*tslint:disable:cyclomatic-complexity*/
    const contentUrl = this.router.url.split('?')[0];
    this.url = pageUrl
      ? `/content/t-mobile/en/${prefix}/${pageUrl}.content.json`
      : `/content/t-mobile/en/${prefix}${contentUrl}.content.json`;
    return this.getRequest(this.url).pipe(
      map((response: any) => {
        if (!response.ok) {
          // tslint:disable-next-line: no-string-literal
          const errorResponse = response.body['errors']['error'];
          this.setAEMGlobalError(errorResponse);
          if (!pageUrl) {
            this.receivedContent.next(null);
          }
          if (critical && this.isErrorHandlingEnabled()) {
            this.httpEventInterceptorService.handleHttpError(response, this.url);
          } else {
            throw new Error();
          }
        }
        const data: Content = response.body;
        this.setPdlData(data, pageUrl, contentUrl, trackPdl);
        if (!pageUrl) {
          if (data.model['model-data']['pdl-enhancements']) {
            this.pdlService.setEventCaptureConfig(
              data.model['model-data']['pdl-enhancements']['event-config']
            );
          }

          this.receivedContent.next(data);
          // set seo metadata in headers
        } else {
          this.pdlService.trackData(pageUrl, 'AEM_COMPONENT_CONFIG', data);
        }
        if (
          data &&
          data.model &&
          data.model['model-data'] &&
          data.model['model-data']['module-enhancements']
        ) {
          return data.model['model-data']['module-enhancements'];
        }
      }),
      catchError((error: Error): Observable<Object> => {
        return this.getAEMRequest(this.url).pipe(
          map((response: HttpResponse<Content>) => {
            const data: Content = response.body;
            if (
              data &&
              data.model &&
              data.model['model-data'] &&
              data.model['model-data']['module-enhancements']
            ) {
              return data.model['model-data']['module-enhancements'];
            }
          })
        );
      })
    );
  }

  /**
   * This is method is used to set the global error messages
   * @returns string
   */
  private setAEMGlobalError(response: APIError[]): void {
    // this.router.navigate([SHARED_CONSTANTS.AEM_ERROR_NAVIGATE]);
  }

  /**
   * Function to identify configurable data from AEM response
   * @param moduleEnhancementsData - AEM Data parsed till module enhancements data
   */
  public getConfigurableData<T>(moduleEnhancementsData: T): object {
    const keys = Object.keys(moduleEnhancementsData);
    for (const key of keys) {
      if (
        moduleEnhancementsData[key].hasOwnProperty('uiComponent') &&
        moduleEnhancementsData[key].uiComponent === 'configurable'
      ) {
        return moduleEnhancementsData[key];
      }
    }
  }

  /**
   * Genaric function to get page content data
   * @returns {Observable<T>}
   */
  public getCMSData(billingType?: string, url?: string): Observable<{}> {
    return this.getContentData(url, billingType).pipe(
      map((contentData: any) => {
        return this.getConfigurableData(contentData);
      }),
      catchError((error: APIError) => {
        return this.errorHandler(error);
      })
    );
  }

  /**
   * Function to identify authorable data from AEM response
   * @param moduleEnhancementsData - AEM Data parsed till module enhancements data
   * @param uiComponent - name of the uiComponent
   * @param identifier - name of the identifier in the uiComponent
   */
  public getAuthorableData(
    moduleEnhancementsData: any,
    uiComponent?: string,
    identifier?: string
  ): object | object[] {
    for (const moduleData of moduleEnhancementsData) {
      if (
        !uiComponent &&
        moduleData.hasOwnProperty('uiComponent') &&
        moduleData.uiComponent === 'configurable'
      ) {
        const moduleDataIndex = moduleEnhancementsData.indexOf(moduleData);
        moduleEnhancementsData.splice(moduleDataIndex, 1);
      } else if (
        moduleData.hasOwnProperty('uiComponent') &&
        moduleData.uiComponent === uiComponent &&
        moduleData.data &&
        moduleData.data.identifier === identifier
      ) {
        return moduleData.data.items;
      }
    }
    return moduleEnhancementsData;
  }

  /**
   * Function to identify authorable data from AEM response
   * @param endpoint - AEM endpoint URL for fetching AEM data
   * @return {Observable<Object>}
   *
   */
  public getAEMRequest(endpoint: string): Observable<HttpResponse<Object>> {
    const domain: string = this.appConfig.getConfig('aemEndPoint');
    const aemVersion = this.appConfig.getConfig('aemContentVersion')
      ? this.appConfig.getConfig('aemContentVersion')
      : '1.0';
    const finalURL = domain + endpoint;
    const defaultHeader = new HttpHeaders().append('Accept', 'application/json');
    const workerArg: WorkerRequestArgs = {
      headers: defaultHeader,
      preferCache: true,
      cacheResponse: true,
      cacheKey: finalURL.concat(aemVersion),
    };
    return this.workerService.get(finalURL, workerArg);
  }
  public retrieveManifest(): Observable<ContentManifest> {
    return this.getContentManifest().pipe(
      map((res: ContentManifest) => {
        this.contentManifest = res;
        this.loaded = true;
        this.ready.next(true);
        return res;
      })
    );
  }

  private getContentManifest(): Observable<ContentManifest> {
    return this.http.get<ContentManifest>(API_MAP.AEM_MANIFEST);
  }

  private getContentPath(id: string): string {
    if (this.contentManifest.content[id]) {
      return this.contentManifest.content[id]['content-path'];
    } else {
      throw new Error(`Could not find content id: ${id}`);
    }
  }

  /**
   * This is method is used to return Facade endpoint from config
   * @returns string
   */
  private isErrorHandlingEnabled(): boolean {
    return this.appConfig.getConfig('isErrorHandlingEnabled');
  }

  /**
   * This Method is used to handling API error
   * @param {APIError} error
   * @returns {Observable<Response>}
   */
  private errorHandler(error: APIError): Observable<Response> {
    return observableThrowError(error);
  }

  /**
   * Function to call SeoService for setting seo data
   * @param data - data which came from AEM
   * @return void
   */
  private setSeoData(data: Content): void {
    if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB && data && data.metadata) {
      this.seoService.setSeoMetadata(data);
    }
  }

  private checkCanonicalUrl(url: string, canonicalUrl: string): boolean {
    return canonicalUrl && url.toLowerCase() === canonicalUrl.toLowerCase();
  }

  public publishMetaBeforeContent(): void {
    this.seoService.publishMetaBeforeContent();
  }
}
